import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface CaseType {
  id: string;
  name: string;
  brand: string | null;
  image_url: string | null;
  price: number | null;
  cost_price: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CaseVariant {
  id: string;
  case_type_id: string;
  iphone_model: string;
  stock_quantity: number;
  created_at: string;
  updated_at: string;
}

export interface CaseTypeWithVariants extends CaseType {
  variants: CaseVariant[];
}

export const useCaseTypes = () => {
  return useQuery({
    queryKey: ['case-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('case_types')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as CaseType[];
    },
  });
};

export const useCaseTypesWithVariants = () => {
  return useQuery({
    queryKey: ['case-types-with-variants'],
    queryFn: async () => {
      const { data: caseTypes, error: caseTypesError } = await supabase
        .from('case_types')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (caseTypesError) throw caseTypesError;

      const { data: variants, error: variantsError } = await supabase
        .from('case_variants')
        .select('*');
      
      if (variantsError) throw variantsError;

      const result: CaseTypeWithVariants[] = (caseTypes as CaseType[]).map(caseType => ({
        ...caseType,
        variants: (variants as CaseVariant[]).filter(v => v.case_type_id === caseType.id),
      }));

      return result;
    },
  });
};

export const useCaseVariants = (caseTypeId?: string) => {
  return useQuery({
    queryKey: ['case-variants', caseTypeId],
    queryFn: async () => {
      let query = supabase.from('case_variants').select('*');
      
      if (caseTypeId) {
        query = query.eq('case_type_id', caseTypeId);
      }
      
      const { data, error } = await query.order('iphone_model', { ascending: true });
      
      if (error) throw error;
      return data as CaseVariant[];
    },
    enabled: !caseTypeId || caseTypeId.length > 0,
  });
};

export const useAddCaseType = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (caseType: { name: string; brand?: string; image_url?: string; price?: number; cost_price?: number }) => {
      const { data, error } = await supabase
        .from('case_types')
        .insert([caseType])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case-types'] });
      queryClient.invalidateQueries({ queryKey: ['case-types-with-variants'] });
      toast.success('تم إضافة نوع الكفر بنجاح');
    },
    onError: () => {
      toast.error('حدث خطأ أثناء إضافة نوع الكفر');
    },
  });
};

export const useUpdateCaseType = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; name?: string; brand?: string; image_url?: string; price?: number; cost_price?: number; is_active?: boolean }) => {
      const { error } = await supabase
        .from('case_types')
        .update(updates)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case-types'] });
      queryClient.invalidateQueries({ queryKey: ['case-types-with-variants'] });
      toast.success('تم تحديث نوع الكفر بنجاح');
    },
    onError: () => {
      toast.error('حدث خطأ أثناء تحديث نوع الكفر');
    },
  });
};

export const useDeleteCaseType = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('case_types')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case-types'] });
      queryClient.invalidateQueries({ queryKey: ['case-types-with-variants'] });
      toast.success('تم حذف نوع الكفر بنجاح');
    },
    onError: () => {
      toast.error('حدث خطأ أثناء حذف نوع الكفر');
    },
  });
};

export const useAddCaseVariant = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (variant: { case_type_id: string; iphone_model: string; stock_quantity: number }) => {
      const { data, error } = await supabase
        .from('case_variants')
        .insert([variant])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case-variants'] });
      queryClient.invalidateQueries({ queryKey: ['case-types-with-variants'] });
      toast.success('تم إضافة المتغير بنجاح');
    },
    onError: (error: any) => {
      if (error?.code === '23505') {
        toast.error('هذا الموديل موجود مسبقاً لهذا الكفر');
      } else {
        toast.error('حدث خطأ أثناء إضافة المتغير');
      }
    },
  });
};

export const useUpdateCaseVariant = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; iphone_model?: string; stock_quantity?: number }) => {
      const { error } = await supabase
        .from('case_variants')
        .update(updates)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case-variants'] });
      queryClient.invalidateQueries({ queryKey: ['case-types-with-variants'] });
      toast.success('تم تحديث المتغير بنجاح');
    },
    onError: () => {
      toast.error('حدث خطأ أثناء تحديث المتغير');
    },
  });
};

export const useDeleteCaseVariant = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('case_variants')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case-variants'] });
      queryClient.invalidateQueries({ queryKey: ['case-types-with-variants'] });
      toast.success('تم حذف المتغير بنجاح');
    },
    onError: () => {
      toast.error('حدث خطأ أثناء حذف المتغير');
    },
  });
};

// Check stock availability
export const checkCaseStock = async (caseTypeId: string, iphoneModel: string, quantity: number = 1): Promise<boolean> => {
  const { data, error } = await supabase
    .from('case_variants')
    .select('stock_quantity')
    .eq('case_type_id', caseTypeId)
    .eq('iphone_model', iphoneModel)
    .maybeSingle();
  
  if (error || !data) return false;
  return data.stock_quantity >= quantity;
};

// Get available stock for a variant
export const getCaseStock = async (caseTypeId: string, iphoneModel: string): Promise<number> => {
  const { data, error } = await supabase
    .from('case_variants')
    .select('stock_quantity')
    .eq('case_type_id', caseTypeId)
    .eq('iphone_model', iphoneModel)
    .maybeSingle();
  
  if (error || !data) return 0;
  return data.stock_quantity;
};
